/**
 * Reprezentare volumetrică a unui câmp scalar 3D.
 *
 * Feliile și rasterele sunt suprafețe: arată câmpul pe un plan, nu în volum.
 * Aici desenăm volumul ca nor de puncte — câte un eșantion per celulă, colorat
 * și făcut mai mult sau mai puțin transparent după valoare. Din orice unghi,
 * suprapunerea punctelor redă densitatea câmpului, deci panașul de poluant sau
 * stratul de nori se citesc ca obiecte tridimensionale, nu ca secțiuni.
 *
 * Spre deosebire de particulele de vânt, geometria e statică între schimbări
 * de pas sau de câmp, deci bufferul se reconstruiește rar, nu în fiecare cadru.
 *
 * Ca și `Wind3DField`, obiectul respectă contractul de primitivă Cesium.
 */
import {
  BlendingState, BoundingSphere, Buffer, BufferUsage, Cartesian3,
  ComponentDatatype, DrawCommand, Matrix4, Pass, PrimitiveType, RenderState,
  ShaderProgram, VertexArray,
} from 'cesium';

// Celulele sub acest prag de opacitate n-ar contribui vizibil, dar ar costa
// la fel de mult; le sărim, ceea ce golește complet zonele fără semnal.
const MIN_ALPHA = 8;

const VERTEX_SHADER = `
in vec3 position;
in vec4 color;
out vec4 v_color;
uniform float u_pointSize;
void main() {
  v_color = color;
  gl_Position = czm_modelViewProjection * vec4(position, 1.0);
  gl_PointSize = u_pointSize;
  czm_vertexLogDepth();
}
`;

// Puncte rotunde cu margine estompată: pătratele s-ar vedea ca grilă.
const FRAGMENT_SHADER = `
in vec4 v_color;
void main() {
  vec2 d = gl_PointCoord - vec2(0.5);
  float r2 = dot(d, d);
  if (r2 > 0.25) discard;
  float falloff = 1.0 - smoothstep(0.0, 0.25, r2);
  out_FragColor = vec4(v_color.rgb, v_color.a * falloff);
  czm_writeLogDepth();
}
`;

export class VolumeCloud {
  /** @param {import('cesium').Viewer} viewer */
  constructor(viewer) {
    this.viewer = viewer;
    this.enabled = false;

    this.exaggeration = 2;
    this.pointSize = 9;
    this.opacity = 0.5;
    this.samplesPerCell = 2;   // eșantioane jitterate per celulă, pentru continuitate

    this._center = Cartesian3.ZERO;
    this._radius = 1;
    this._count = 0;

    this._positions = null;
    this._colors = null;
    this._capacity = 0;

    this._inScene = false;
    this._destroyed = false;
    this._dirty = false;

    this._va = null;
    this._sp = null;
    this._rs = null;
    this._command = null;
    this._posBuffer = null;
    this._colBuffer = null;
  }

  /**
   * Reconstruiește norul din volumul dat.
   *
   * @param {{data: ArrayLike<number>, shape: number[]}} cube
   * @param {object} grid  { lats, lons, altitudes }
   * @param {(value:number)=>number[]} colorFn
   */
  rebuild(cube, { lats, lons, altitudes }, colorFn) {
    const [nlev, nlat, nlon] = cube.shape;
    const per = Math.max(1, this.samplesPerCell);

    // Originea locală: pozițiile pleacă relativ la ea, ca Float32 să aibă
    // precizie suficientă (ECEF absolut ar tremura la nivel de metri).
    const midLon = (lons[0] + lons[lons.length - 1]) / 2;
    const midLat = (lats[0] + lats[lats.length - 1]) / 2;
    const topM = altitudes[altitudes.length - 1] * this.exaggeration;
    this._center = Cartesian3.fromDegrees(midLon, midLat, topM / 2);
    this._radius = Cartesian3.distance(
      this._center,
      Cartesian3.fromDegrees(lons[0], lats[0], topM),
    ) * 1.25;

    const maxPoints = nlev * nlat * nlon * per;
    if (this._capacity < maxPoints) {
      this._positions = new Float32Array(maxPoints * 3);
      this._colors = new Uint8Array(maxPoints * 4);
      this._capacity = maxPoints;
      // Bufferele existente nu mai încap: le recreăm la următorul cadru.
      this._releaseGl();
    }

    const dLat = nlat > 1 ? (lats[1] - lats[0]) : 0;
    const dLon = nlon > 1 ? (lons[1] - lons[0]) : 0;

    const pos = this._positions;
    const col = this._colors;
    const scratch = new Cartesian3();
    let n = 0;

    for (let l = 0; l < nlev; l++) {
      const alt = altitudes[l];
      // Grosimea stratului, pentru jitter pe verticală
      const below = l > 0 ? altitudes[l - 1] : alt;
      const above = l < nlev - 1 ? altitudes[l + 1] : alt;
      const spanUp = (above - alt) / 2;
      const spanDown = (alt - below) / 2;
      const levOffset = l * nlat * nlon;

      for (let y = 0; y < nlat; y++) {
        for (let x = 0; x < nlon; x++) {
          const value = cube.data[levOffset + y * nlon + x];
          if (value == null || isNaN(value)) continue;

          const rgba = colorFn(value);
          const a = rgba[3] * this.opacity;
          if (a < MIN_ALPHA) continue;

          for (let s = 0; s < per; s++) {
            const jLat = (Math.random() - 0.5) * dLat;
            const jLon = (Math.random() - 0.5) * dLon;
            const jAlt = (Math.random() - 0.5) * 2 * (Math.random() < 0.5 ? spanDown : spanUp);

            Cartesian3.fromDegrees(
              lons[x] + jLon, lats[y] + jLat,
              (alt + jAlt) * this.exaggeration,
              undefined, scratch,
            );
            pos[n * 3] = scratch.x - this._center.x;
            pos[n * 3 + 1] = scratch.y - this._center.y;
            pos[n * 3 + 2] = scratch.z - this._center.z;

            col[n * 4] = rgba[0];
            col[n * 4 + 1] = rgba[1];
            col[n * 4 + 2] = rgba[2];
            col[n * 4 + 3] = a;
            n++;
          }
        }
      }
    }

    this._count = n;
    this._dirty = true;
    if (this._command) {
      this._command.count = n;
      this._command.boundingVolume = new BoundingSphere(this._center, this._radius);
      this._command.modelMatrix = Matrix4.fromTranslation(this._center);
    }
    this.viewer.scene.requestRender();
    return n;
  }

  get pointCount() {
    return this._count;
  }

  show() {
    this.enabled = true;
    if (!this._inScene) {
      this.viewer.scene.primitives.add(this);
      this._inScene = true;
    }
    this.viewer.scene.requestRender();
  }

  hide() {
    this.enabled = false;
    this.viewer.scene.requestRender();
  }

  // ── Contract de primitivă Cesium ────────────────────────────────
  update(frameState) {
    if (!this.enabled || this._destroyed || this._count === 0) return;

    const context = frameState.context;
    if (!this._command) this._createResources(context);

    if (this._dirty) {
      // Încărcăm doar porțiunea folosită, nu tot bufferul alocat.
      this._posBuffer.copyFromArrayView(
        this._positions.subarray(0, this._count * 3), 0);
      this._colBuffer.copyFromArrayView(
        this._colors.subarray(0, this._count * 4), 0);
      this._command.count = this._count;
      this._dirty = false;
    }

    frameState.commandList.push(this._command);
  }

  _createResources(context) {
    this._posBuffer = Buffer.createVertexBuffer({
      context, typedArray: this._positions, usage: BufferUsage.STATIC_DRAW,
    });
    this._colBuffer = Buffer.createVertexBuffer({
      context, typedArray: this._colors, usage: BufferUsage.STATIC_DRAW,
    });

    this._va = new VertexArray({
      context,
      attributes: [
        {
          index: 0, vertexBuffer: this._posBuffer,
          componentsPerAttribute: 3, componentDatatype: ComponentDatatype.FLOAT,
        },
        {
          index: 1, vertexBuffer: this._colBuffer,
          componentsPerAttribute: 4, componentDatatype: ComponentDatatype.UNSIGNED_BYTE,
          normalize: true,
        },
      ],
    });

    this._sp = ShaderProgram.fromCache({
      context,
      vertexShaderSource: VERTEX_SHADER,
      fragmentShaderSource: FRAGMENT_SHADER,
      attributeLocations: { position: 0, color: 1 },
    });

    this._rs = RenderState.fromCache({
      depthTest: { enabled: true },
      // Punctele nu se ocluzionează între ele: altfel norul ar avea găuri
      // în funcție de ordinea de desenare.
      depthMask: false,
      blending: BlendingState.ALPHA_BLEND,
    });

    this._command = new DrawCommand({
      vertexArray: this._va,
      shaderProgram: this._sp,
      renderState: this._rs,
      pass: Pass.TRANSLUCENT,
      primitiveType: PrimitiveType.POINTS,
      modelMatrix: Matrix4.fromTranslation(this._center),
      // Obligatorie: Cesium sortează comenzile translucide după distanță.
      boundingVolume: new BoundingSphere(this._center, this._radius),
      count: this._count,
      owner: this,
      uniformMap: {
        u_pointSize: () => this.pointSize,
      },
    });
  }

  _releaseGl() {
    if (this._va) this._va.destroy();
    if (this._sp) this._sp.destroy();
    this._va = null;
    this._sp = null;
    this._command = null;
    this._posBuffer = null;
    this._colBuffer = null;
  }

  isDestroyed() {
    return this._destroyed;
  }

  destroy() {
    this._destroyed = true;
    this._releaseGl();
    return undefined;
  }
}
